// WS5 PR3.3a — viewbox switcher UI.
//
// Strip orizzontale (bottom-center) con N pill, una per viewbox. Click/tap
// chiama `onSelect(arrayIdx)` (orchestrator.selectViewbox PR3.2b). Keyboard
// nav: arrows + Home/End + 1-9 quick-jump.
//
// Visibility: nascosto se firstPersonAvailable=false (scena legacy senza
// viewboxes) o `available.length < 2` (no senso switch su 1-only).
//
// a11y: role="tablist", aria-selected su corrente, aria-disabled durante
// gliding (Codex PR3.1 W3: durante teleport phase 'gliding' i click sono
// no-op nel reducer — la UI deve riflettere lo stato per non far sembrare
// click persi).

import { t } from '../../i18n/init.js';
import type { ViewboxSnapshot } from '../../viewbox/types.js';
import type { ComponentInstance } from '../component.js';

export interface ViewboxSwitcherProps {
	snapshot: ViewboxSnapshot;
	embed: boolean;
	onSelect: (arrayIdx: number) => void;
}

const PILL_BG = 'rgba(15,15,17,0.85)';
const PILL_BORDER_ACTIVE = 'rgba(96,165,250,0.55)';
const PILL_BORDER_IDLE = 'rgba(255,255,255,0.10)';
const PILL_BG_ACTIVE = 'rgba(96,165,250,0.18)';

function makePill(
	label: string,
	index: number,
	active: boolean,
	disabled: boolean,
	onActivate: () => void,
): HTMLButtonElement {
	const btn = document.createElement('button');
	btn.type = 'button';
	btn.setAttribute('role', 'tab');
	btn.setAttribute('aria-selected', active ? 'true' : 'false');
	btn.setAttribute('tabindex', active ? '0' : '-1');
	btn.dataset['viewboxArrayIdx'] = String(index);
	const numberPrefix = index < 9 ? `${index + 1}` : '';
	btn.textContent = numberPrefix ? `${numberPrefix} · ${label}` : label;
	btn.style.cssText = [
		`background:${active ? PILL_BG_ACTIVE : PILL_BG}`,
		`border:1px solid ${active ? PILL_BORDER_ACTIVE : PILL_BORDER_IDLE}`,
		`color:${active ? '#bfdbfe' : '#d4d4d8'}`,
		'padding:0.375rem 0.75rem',
		'border-radius:9999px',
		'font-family:system-ui,sans-serif',
		'font-size:0.75rem',
		'cursor:pointer',
		'pointer-events:auto',
		'transition:background 120ms ease, border-color 120ms ease',
		'white-space:nowrap',
		'max-width:9rem',
		'overflow:hidden',
		'text-overflow:ellipsis',
	].join(';');
	if (disabled) {
		btn.setAttribute('aria-disabled', 'true');
		btn.style.opacity = '0.6';
		btn.style.cursor = 'not-allowed';
	}
	btn.addEventListener('click', () => {
		if (disabled || active) return;
		onActivate();
	});
	return btn;
}

/** Guard input/contenteditable + modifier (Codex PR3.3a-comp W2). Pattern
 *  allineato a `src/lib/keyboard.ts:29-34` per coerenza con il resto dei
 *  shortcut viewer. */
function isKeyEventActionable(e: KeyboardEvent): boolean {
	if (e.ctrlKey || e.metaKey || e.altKey) return false;
	const targetEl = e.target as HTMLElement | null;
	if (targetEl !== null) {
		const tag = targetEl.tagName?.toLowerCase();
		if (tag === 'input' || tag === 'textarea' || targetEl.isContentEditable) return false;
	}
	return true;
}

export function mountViewboxSwitcher(
	root: HTMLElement,
	initial: ViewboxSwitcherProps,
): ComponentInstance<ViewboxSwitcherProps> {
	const wrap = document.createElement('nav');
	wrap.classList.add('viewer-ui-interactive');
	wrap.setAttribute('role', 'tablist');
	wrap.setAttribute('aria-label', t('viewer.viewbox.aria_tablist'));
	// Posizionamento: bottom-center, SOPRA il walk-hud WASD overlay quando
	// walk e' attivo. z-index in linea col walk-hud (11).
	wrap.style.cssText = initial.embed
		? 'position:fixed;bottom:4rem;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:0.5rem;max-width:90vw;overflow-x:auto;padding:0.25rem;z-index:11;pointer-events:none'
		: 'position:fixed;bottom:6rem;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:0.5rem;max-width:90vw;overflow-x:auto;padding:0.25rem;z-index:11;pointer-events:none';
	wrap.style.display = 'none';

	const label = document.createElement('span');
	label.style.cssText =
		'color:#a1a1aa;font-family:system-ui,sans-serif;font-size:0.6875rem;text-transform:uppercase;letter-spacing:0.05em;padding:0 0.375rem;pointer-events:none';
	label.textContent = t('viewer.viewbox.switcher_label');
	wrap.appendChild(label);

	const list = document.createElement('div');
	list.style.cssText = 'display:flex;gap:0.375rem;align-items:center';
	wrap.appendChild(list);

	root.appendChild(wrap);

	// Keyboard nav: arrows + Home/End + 1-9.
	// Codex PR3.3a-comp W1: no-op anche da tastiera durante teleport gliding.
	// Codex W2: guard modifier + input/contenteditable allineato keyboard.ts.
	// Codex W3: usa `currentOnSelect` aggiornato in update() invece di
	// initial.onSelect (callback puo' cambiare tra mount e re-render).
	const onKey = (ev: KeyboardEvent): void => {
		if (wrap.style.display === 'none') return;
		if (!isKeyEventActionable(ev)) return;
		if (currentSnapshot.teleportPhase === 'gliding') return;
		const num = parseInt(ev.key, 10);
		if (Number.isInteger(num) && num >= 1 && num <= 9) {
			const targetIdx = num - 1;
			if (targetIdx < currentSnapshot.available.length && targetIdx !== currentSnapshot.currentArrayIdx) {
				ev.preventDefault();
				currentOnSelect(targetIdx);
			}
			return;
		}
		// Arrow/Home/End funzionano solo se focus e' dentro il switcher.
		if (!wrap.contains(document.activeElement)) return;
		const total = currentSnapshot.available.length;
		if (total === 0) return;
		const cur = currentSnapshot.currentArrayIdx ?? 0;
		let next: number | null = null;
		if (ev.key === 'ArrowRight') next = (cur + 1) % total;
		else if (ev.key === 'ArrowLeft') next = (cur - 1 + total) % total;
		else if (ev.key === 'Home') next = 0;
		else if (ev.key === 'End') next = total - 1;
		if (next !== null) {
			ev.preventDefault();
			if (next !== cur) currentOnSelect(next);
		}
	};
	let currentSnapshot: ViewboxSnapshot = initial.snapshot;
	let currentOnSelect: (arrayIdx: number) => void = initial.onSelect;
	document.addEventListener('keydown', onKey);

	const render = (snapshot: ViewboxSnapshot): void => {
		currentSnapshot = snapshot;
		// Visibility: nascosto se nessuna viewbox o 1-solo (no senso switch).
		const visible = snapshot.firstPersonAvailable && snapshot.available.length >= 2;
		wrap.style.display = visible ? 'flex' : 'none';
		if (!visible) return;
		list.innerHTML = '';
		const disabled = snapshot.teleportPhase === 'gliding';
		for (let i = 0; i < snapshot.available.length; i++) {
			const vb = snapshot.available[i];
			if (vb === undefined) continue;
			const active = i === snapshot.currentArrayIdx;
			// Codex W3: usa currentOnSelect (aggiornato in update()) per
			// permettere al renderer di sostituire callback senza re-mount.
			const pill = makePill(vb.name, i, active, disabled, () => currentOnSelect(i));
			list.appendChild(pill);
		}
	};

	render(initial.snapshot);

	return {
		update(props: ViewboxSwitcherProps) {
			// Codex W3: sostituisci callback (può cambiare tra renderState).
			currentOnSelect = props.onSelect;
			render(props.snapshot);
		},
		cleanup() {
			document.removeEventListener('keydown', onKey);
			wrap.remove();
		},
	};
}
